import type { DashboardRole } from '@/shared/types/session';
import type { TenantSubtype } from '@propertyscan/shared/types';

/**
 * Derive the UI role id used to pick a sidebar variant + topbar label.
 *
 * `DashboardRole` describes a user's access level inside a tenant; `TenantSubtype`
 * describes the kind of tenant. The UI variants align with the latter, except
 * for `super_admin` and `builder_user`, which are resolved from the role:
 *   - `super_admin` is a platform-wide role independent of any tenant.
 *   - `builder_user` (an invited builder) shares the inviting developer's
 *     tenant, so the subtype is 'developer' — but their UI must be the Builder
 *     dashboard, so the membership role wins.
 */
export type DashboardRoleId = 'agent' | 'team' | 'builder' | 'developer' | 'admin';

export function resolveDashboardRoleId(
    role: DashboardRole,
    subtype: TenantSubtype | null,
): DashboardRoleId {
    if (role === 'super_admin')  return 'admin';
    if (role === 'builder_user') return 'builder';
    // An invited sales-team member shares the inviting tenant, but their UI must
    // be the Team / Brokerage dashboard, so the membership role wins (mirrors
    // the builder_user case above).
    if (role === 'sales_rep')    return 'team';
    switch (subtype) {
        case 'solo_agent': return 'agent';
        case 'team':       return 'team';
        case 'builder':    return 'builder';
        case 'developer':  return 'developer';
        default:           return 'agent';
    }
}

export const ROLE_LABEL: Record<DashboardRoleId, string> = {
    agent:     'Realtor',
    team:      'Team / Brokerage',
    builder:   'Builder',
    developer: 'Developer',
    admin:     'Super Admin',
};

/**
 * The label shown for a signed-in member (topbar, sidebar heading, workspace
 * switcher).
 *
 * Prefer this over `ROLE_LABEL[roleId]`: the 'team' VARIANT is shared by two
 * very different members — the owner of a Team/Brokerage workspace, and a
 * Sales Team member inside any workspace (a rep in a Developer workspace would
 * otherwise be mislabelled "Team / Brokerage"). The variant decides the
 * layout; this decides what the person is called.
 */
export function resolveRoleLabel(role: DashboardRole, subtype: TenantSubtype | null): string {
    if (role === 'sales_rep') return 'Sales Team';
    return ROLE_LABEL[resolveDashboardRoleId(role, subtype)];
}
